/**
 * `gmDamage` — the host-agnostic **GM-adjudicated damage model**: the pure seam between what a GM
 * typed into the palette band and the `request_gm_damage` intent the encounter room resolves.
 * Same discipline `tracks.ts` and `status.ts` follow, and for the same reason — the interesting
 * logic (what counts as a valid amount, which control is meaningful for which type) is testable
 * without a DOM, so the band component above it stays thin enough to read in one pass.
 *
 * **What this is NOT: a second wall.** Every rule here is mirrored from the referee's own gate
 * (`packages/rules/src/encounter/referee/refereeGmDamage.ts`) as an *affordance*, so the GM sees a
 * disabled button instead of firing a command that bounces. The server stays the authority — an
 * amount that slips through is rejected there, and the band surfaces that rejection like any other.
 *
 * **`ap` is inert for burn and heat, and the band says so rather than silently ignoring it.**
 * `mitigate`'s own `ignoresArmor` already forces armor off for those two, so an AP checkbox would
 * be a control that does nothing — the worst kind, because it teaches a GM a rule that isn't
 * true. {@link apMeaningful} is what lets the band disable it with a stated reason.
 */
import type { CombatantId } from "@lancer-vtt/domain";
import type { GmDamageType, RequestGmDamage } from "@lancer-vtt/shared";

/**
 * The types a GM may adjudicate, in the order the band renders them: the three physical types
 * first (the overwhelmingly common case), then the two that route somewhere other than plain HP.
 *
 * Deliberately NOT re-derived from `GmDamageType` at runtime — a union has no runtime form. The
 * `satisfies` below is what keeps this list honest: drop a member and it still compiles, but
 * `gmDamage.test.ts` pins the count, and adding a member the union doesn't have is a type error.
 */
export const GM_DAMAGE_TYPES = ["kinetic", "energy", "explosive", "burn", "heat"] as const satisfies readonly GmDamageType[];

/**
 * Does the AP flag mean anything for this damage type? Burn and heat ignore armor unconditionally
 * (RAW, and `mitigate`'s `ignoresArmor`), so AP is a no-op for them. The band reads this to
 * disable the control rather than let a GM tick a box that changes nothing.
 */
export function apMeaningful(dtype: GmDamageType): boolean {
  return dtype !== "burn" && dtype !== "heat";
}

/**
 * Where this damage type actually lands — the one-line hint the band shows under the type picker.
 * Worth surfacing because the three destinations are genuinely different and a GM picking "heat"
 * expecting HP loss has mis-adjudicated, not mis-clicked.
 */
export function damageDestination(dtype: GmDamageType): string {
  if (dtype === "heat") return "heat track → Overheating, never HP";
  if (dtype === "burn") return "HP now, ignores armor, and marks Burn";
  return "Overshield, then HP";
}

/** A parsed, validated adjudication — or the reason it isn't one yet. */
export type GmDamageDraft =
  | { ok: true; amount: number }
  | { ok: false; error: string };

/**
 * Parse the amount field. Returns the reason rather than a bare boolean so the band can state it,
 * and treats an EMPTY field as "not ready" rather than zero — the same `Number("") === 0` trap
 * that made the HP stepper commit phantom zeroes lives here too, and a GM mid-type has not asked
 * for anything yet.
 */
export function parseAmount(raw: string): GmDamageDraft {
  const trimmed = raw.trim();
  if (trimmed === "") return { ok: false, error: "enter an amount" };
  const n = Number(trimmed);
  if (!Number.isInteger(n)) return { ok: false, error: "amount must be a whole number" };
  if (n < 0) return { ok: false, error: "amount must not be negative" };
  return { ok: true, amount: n };
}

export type GmDamageIntentArgs = {
  target: CombatantId;
  amount: number;
  damageType: GmDamageType;
  /** The AP tick. Dropped for a type where it is inert, so the wire never carries a lie. */
  ap: boolean;
};

/**
 * Build the intent. `ap` is normalised away for burn/heat here rather than at the band, so the
 * wire payload can never claim an armor-piercing burn — the referee would ignore it, but a
 * recorded intent that says something untrue is a bad forward-log entry and a worse replay.
 */
export function gmDamageIntent(args: GmDamageIntentArgs): RequestGmDamage {
  const { target, amount, damageType, ap } = args;
  return {
    type: "request_gm_damage",
    target,
    amount,
    damageType,
    ...(ap && apMeaningful(damageType) ? { ap: true } : {}),
  };
}
